import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  Column,
  FormController,
  ITabs,
  MuiBox,
  MuiGrid,
  MuiModal,
  MuiTable,
  MuiTabPanel,
  MuiTabs,
  MuiTextField,
  MuiTypography,
  WarningConfirmationModal,
} from "../../../../components";
import { useSiteCore } from "../../../../hooks/useSiteCore";

import {
  ClientSpecificContent,
  ClientSpecificContentFormKey,
  ProgramTemplate,
  ProgramTemplateFormKey,
  Resource,
  ResourcesFormKey,
} from "../../modelsAndSchemas/Models";

import {
  useAddClientSpecificContentResourcesAndProgramTemplates,
  useGetCountryListForDropdowns,
  useUpdateClientSpecificContentMutation,
} from "../../../../api/services/client/clientApis";
import {
  ResourcesAndProgramTemplatesParams,
  UpdateResourcesAndProgramTemplatesParams,
} from "../../../../api/services/client/models";
import { useGetProgramTemplateMutation } from "../../../../api/services/programTemplate/programTemplateApis";
import {
  BE_ERROR_CODES,
  DUPLICATE_DATA_VALUES,
  DuplicateDataTypes,
  MAX_LENGTH_75,
  PAGE_IDS,
} from "../../../../helpers/constants";
import { COMPONENT_IDS } from "../../../../helpers/constants/componentIds";
import {
  findFirstDuplicateData,
  getCountryIdsAsStringArray,
  getParsedBEErrors,
  hasCountryNameKey,
  hasKey,
  isEmpty,
  isNotEmpty,
  replaceHashWithValue,
  setApiDataToDropDownOption,
} from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../../../redux/slices/client/createClientSelector";
import {
  CreateProgramTemplateValidationSchema,
  CreateResourceValidationSchema,
} from "../../modelsAndSchemas/ValidationSchema";
import {
  clientSpecificContentKeys,
  createClientSiteCoreKeys,
} from "../../siteCore/SiteCoreKeys";
import { AddClientSpecificContentWrapper } from "./AddClientSpecificContent.style";
import {
  CreateProgramTemplates,
  emptyProgramTemplateItem,
} from "./createProgramTemplate/CreateProgramTemplates";
import {
  CreateResources,
  emptyResourceItem,
} from "./createResource/CreateResource";

interface AddClientSpecificContentProps {
  show: boolean;
  onClose: () => void;
  onClientSpecificContentAdded?: (groupId?: string) => void;
  isEdit?: boolean;
  clientSpecificContentData?: ClientSpecificContent;
}

const pageId = PAGE_IDS.ADD_CLIENT_SPECIFIC_CONTENT;
const resourceMapId = "clientResourceId";

export const AddClientSpecificContent: FC<AddClientSpecificContentProps> = ({
  show,
  onClose,
  onClientSpecificContentAdded,
  isEdit = false,
  clientSpecificContentData,
}) => {
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [duplicateErrorMessage, setDuplicateErrorMessage] = useState<string>();
  const [showCancel, setShowCancel] = useState(false);
  const isCancelButtonClickedRef = useRef(false);
  const { id: clientId } = useSelector(createClientSelector);
  const { commonLabels, fetchLabel, commonValidationMessages } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const {
    next,
    save,
    cancel,
    no,
    yes,
    ok,
    update,
    cancelActionText,
    inputPlaceHolder,
    loadingPleaseWait,
  } = commonLabels;

  const {
    clientSpecificContentTitle,
    resourcesTab,
    programTemplatesTab,
    addClientSpecificContentHeading,
    editClientSpecificContentHeading,
    saveClientSpecificContentActionText,
    success,
    successMessage,
    groupName,
    groupUpdateActionText,
    groupUpdatedMessage,
    duplicateRecordResourceError,
    duplicateRecordProgramTemplateError,
    uniqueGroupNameError,
  } = clientSpecificContentKeys;

  const getLabel = (key: string) => {
    return fetchLabel(clientSpecificContentTitle, key);
  };

  const {
    mutateAsync: getProgramTemplateDropdownData,
    data: programTemplateList,
  } = useGetProgramTemplateMutation();

  const { data: countryListData } = useGetCountryListForDropdowns();

  useEffect(() => {
    getProgramTemplateDropdownData();
  }, []);

  const {
    mutateAsync: addResourcesAndProgramTemplates,
    data: addResourcesAndProgramTemplatesData,
    isPending: isSavePending,
    isError: isSaveError,
    isSuccess: isSaveSuccess,
    error: addResourcesAndProgramTemplatesError,
  } = useAddClientSpecificContentResourcesAndProgramTemplates();

  const {
    mutateAsync: updateResourcesAndProgramTemplates,
    data: updateResourcesAndProgramTemplatesData,
    isPending: isUpdatePending,
    isError: isUpdateError,
    isSuccess: isUpdateSuccess,
    error: updateResourcesAndProgramTemplatesError,
  } = useUpdateClientSpecificContentMutation();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isSavePending || isUpdatePending,
        message: loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isSavePending, isUpdatePending]);

  useEffect(() => {
    if (
      isUpdateSuccess &&
      updateResourcesAndProgramTemplatesData &&
      isNotEmpty(updateResourcesAndProgramTemplatesData)
    ) {
      onClose();
      onClientSpecificContentAdded?.(
        clientSpecificContentData?.clientResourceGroupId,
      );
      setTimeout(() => {
        dispatch(
          toggleToast({
            open: true,
            message: getLabel(groupUpdatedMessage),
            type: "success",
          }),
        );
      }, 100);
    }
  }, [updateResourcesAndProgramTemplatesData, isUpdateSuccess]);

  useEffect(() => {
    if (isSaveSuccess && isNotEmpty(addResourcesAndProgramTemplatesData)) {
      setShowSuccessModal(true);
    }
  }, [isSaveSuccess]);

  useEffect(() => {
    if (isSaveError || isUpdateError) {
      dispatch(toggleLoader({ isVisible: false }));
      const errorObj = getParsedBEErrors(
        isSaveError
          ? addResourcesAndProgramTemplatesError
          : updateResourcesAndProgramTemplatesError,
      );

      if (
        isNotEmpty(errorObj) &&
        hasKey(errorObj[0], "errorCode") &&
        errorObj[0].errorCode === BE_ERROR_CODES.GROUP_NAME_UNIQUE
      ) {
        setProgramTemplateError(ProgramTemplateFormKey.groupName, {
          type: "custom",
          message: getLabel(uniqueGroupNameError),
        });
      }
    }
  }, [isSaveError, isUpdateError]);

  useEffect(() => {
    if (
      isEdit &&
      clientSpecificContentData &&
      isNotEmpty(clientSpecificContentData)
    ) {
      setResourceValues(
        ResourcesFormKey.resources,
        clientSpecificContentData.resources,
      );

      setProgramTemplateValues(
        ProgramTemplateFormKey.programTemplates,
        clientSpecificContentData.programTemplates,
      );
      setProgramTemplateValues(
        ProgramTemplateFormKey.groupName,
        clientSpecificContentData.clientResourceGroupName,
      );
    }
  }, [isEdit, clientSpecificContentData]);

  const tabIdResources = `${pageId}-tab1`;
  const tabIdProgramTemplates = `${pageId}-tab2`;
  const tabs: ITabs[] = [
    {
      id: tabIdResources,
      label: getLabel(resourcesTab),
    },
    {
      id: tabIdProgramTemplates,
      label: getLabel(programTemplatesTab),
    },
  ];

  //form Resources tab
  const resourcesMethods = useForm<any>({
    mode: "onBlur",
    defaultValues: {
      [ResourcesFormKey.resources]: [emptyResourceItem],
    },
    resolver: yupResolver(
      CreateResourceValidationSchema(commonValidationMessages),
    ),
  });

  const {
    handleSubmit: handleSubmitResources,
    getValues: getResourceTabValues,
    setValue: setResourceValues,
    formState: { isDirty: isResourceFormDirty },
  } = resourcesMethods;

  //form for Program Templates tab
  const programTemplateMethods = useForm<any>({
    mode: "onBlur",
    defaultValues: {
      [ProgramTemplateFormKey.groupName]: "",
      [ProgramTemplateFormKey.programTemplates]: [emptyProgramTemplateItem],
    },
    resolver: yupResolver(
      CreateProgramTemplateValidationSchema(commonValidationMessages),
    ),
  });

  const {
    handleSubmit: handleSubmitProgramTemplates,
    getValues: getProgramTemplateTabValues,
    formState: {
      isDirty: isProgramTemplateFormDirty,
      isValid: isProgramTemplateFormValid,
      errors,
    },
    setValue: setProgramTemplateValues,
    setError: setProgramTemplateError,
  } = programTemplateMethods;

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    if (value === 1) {
      onConfirmClick(); // check if user entered mandatory fields or not
    } else {
      setSelectedTab(value);
    }
  };

  const isTypeResource = (type: DuplicateDataTypes) =>
    type === DUPLICATE_DATA_VALUES.CLIENT_SPECIFIC_RESOURCE;

  const hasDuplicateData = (
    data: Resource[] | ProgramTemplate[],
    type: DuplicateDataTypes,
  ) => {
    const duplicateData = findFirstDuplicateData<Resource | ProgramTemplate>(
      data,
      type,
      undefined,
      isEdit
        ? isTypeResource(type)
          ? [resourceMapId]
          : [
              ProgramTemplateFormKey.resourceGroupProgramTemplateMapId,
              ProgramTemplateFormKey.showCountry,
              ProgramTemplateFormKey.allCountries,
              ProgramTemplateFormKey.programTemplateDropdownChangeStatus,
            ]
        : undefined,
    );

    if (duplicateData?.duplicateIndexes) {
      const label = getLabel(
        duplicateData.type === DUPLICATE_DATA_VALUES.CLIENT_SPECIFIC_RESOURCE
          ? duplicateRecordResourceError
          : duplicateRecordProgramTemplateError,
      );
      const errorMessageFromSitecore = replaceHashWithValue(label, {
        NUMBER1: (duplicateData.duplicateIndexes[0] + 1).toString(),
        NUMBER2: (duplicateData.duplicateIndexes[1] + 1).toString(),
      });
      setDuplicateErrorMessage(errorMessageFromSitecore);
    } else {
      if (isNotEmpty(duplicateErrorMessage)) {
        setDuplicateErrorMessage(undefined);
      }
    }
    return isNotEmpty(duplicateData);
  };

  //first tab click
  const onNextClick = () => {
    const { resources } = getResourceTabValues();
    const hasDuplicateRecords = hasDuplicateData(
      resources,
      DUPLICATE_DATA_VALUES.CLIENT_SPECIFIC_RESOURCE,
    );
    if (!hasDuplicateRecords) {
      setSelectedTab(1);
    }
  };

  const getUpdatedProgramTemplates = () => {
    const { programTemplates } = getProgramTemplateTabValues();
    const updatedProgramTemplates = programTemplates.map(
      (programTemplate: ProgramTemplate) => ({
        ...programTemplate,
        countries: hasCountryNameKey(programTemplate.countries)
          ? getCountryIdsAsStringArray(programTemplate.countries)
          : programTemplate.countries,
      }),
    );

    return updatedProgramTemplates;
  };

  //second tab click
  const onFinalSubmitClick = () => {
    const { resources } = getResourceTabValues();
    const { programTemplates, groupName } = getProgramTemplateTabValues();

    const params: ResourcesAndProgramTemplatesParams = {
      clientId,
      groupName,
      programTemplates,
      resources,
    };
    if (isEdit && clientSpecificContentData?.clientResourceGroupId) {
      const updatedParams: UpdateResourcesAndProgramTemplatesParams = {
        ...params,
        clientProfileId: clientId,
        programTemplates: getUpdatedProgramTemplates(),
        groupId: clientSpecificContentData.clientResourceGroupId,
      };
      updateResourcesAndProgramTemplates(updatedParams);
    } else {
      addResourcesAndProgramTemplates(params);
    }
  };

  const isFirstTabSelected = useMemo(() => selectedTab === 0, [selectedTab]);

  const onConfirmClick = () => {
    if (isFirstTabSelected) {
      handleSubmitResources(onNextClick)();
    } else {
      if (isProgramTemplateFormValid && isEmpty(errors)) {
        const { programTemplates } = getProgramTemplateTabValues();
        const hasDuplicateRecords = hasDuplicateData(
          isEdit ? getUpdatedProgramTemplates() : programTemplates,
          DUPLICATE_DATA_VALUES.CLIENT_SPECIFIC_PROGRAM_TEMPLATE,
        );
        if (!hasDuplicateRecords) {
          isCancelButtonClickedRef.current = false;
          setShowCancel(true);
        }
      } else {
        if (errors?.groupName) {
          return;
        }
        handleSubmitProgramTemplates(onFinalSubmitClick)();
      }
    }
  };

  const closeModalHandler = () => setShowCancel(false);

  const cancelMainModalHandler = () => {
    if (isResourceFormDirty || isProgramTemplateFormDirty) {
      isCancelButtonClickedRef.current = true;
      setShowCancel(true);
    } else {
      onClose();
    }
  };

  const cancelConfirmHandler = () => {
    if (isCancelButtonClickedRef.current) {
      setShowCancel(false);
      onClose();
      isCancelButtonClickedRef.current = false;
    } else {
      handleSubmitProgramTemplates(onFinalSubmitClick)();
      setShowCancel(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
  };

  const handleSuccessModalPrimaryClick = () => {
    handleSuccessModalClose();
    onClose();
    onClientSpecificContentAdded?.();
  };

  const getTableDisplayData = () => {
    const data = addResourcesAndProgramTemplatesData?.data;
    const resources = data.resources;
    const programTemplates = data.programTemplates;

    const maxLength = Math.max(resources.length, programTemplates.length);
    const resultArray = Array.from({ length: maxLength }, (_, index) => ({
      clientResourceGroupName: index === 0 ? data.clientResourceGroupName : "",
      programTemplateName: programTemplates[index]?.programTemplateName || "",
      resourceName: resources[index]?.resourceName || "",
    }));
    return resultArray;
  };

  const columns: Column[] = [
    {
      id: ClientSpecificContentFormKey.clientResourceGroupName,
      label: getLabel(groupName),
      render: (row: any) => (
        <MuiTypography variant="h3">
          {row.clientResourceGroupName}
        </MuiTypography>
      ),
      isSortVisible: false,
    },
    {
      id: ResourcesFormKey.resourceName,
      label: getLabel(createClientSiteCoreKeys.resourceName),
      isSortVisible: false,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">{row.resourceName}</MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ProgramTemplateFormKey.programTemplateId,
      label: getLabel(createClientSiteCoreKeys.programTemplate),
      isSortVisible: false,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">{row.programTemplateName}</MuiTypography>
        </MuiBox>
      ),
    },
  ];

  const modalFinalButtonTitle = useMemo(
    () => (isEdit ? update : save),
    [isEdit],
  );

  const getModalTitle = () => {
    return isEdit
      ? replaceHashWithValue(
          getLabel(editClientSpecificContentHeading),
          clientSpecificContentData?.clientResourceGroupName ?? "",
        )
      : getLabel(addClientSpecificContentHeading);
  };

  return (
    <MuiModal
      id={COMPONENT_IDS.COMMON_MODAL}
      open={show}
      onClose={cancelMainModalHandler}
      title={getModalTitle()}
      primaryButtonTitle={isFirstTabSelected ? next : modalFinalButtonTitle}
      secondaryButtonTitle={cancel}
      onPrimaryButtonClick={onConfirmClick}
      onSecondaryButtonClick={cancelMainModalHandler}
      footerErrorMessage={duplicateErrorMessage}
      hasLargeContent
    >
      <AddClientSpecificContentWrapper>
        <MuiGrid size={{ md: 4, sm: 6, xs: 12 }} container>
          <MuiBox className="group-name-container">
            <FormController
              control={programTemplateMethods.control}
              name={ProgramTemplateFormKey.groupName}
            >
              <MuiTextField
                label={getLabel(groupName)}
                id={`${pageId}-${ProgramTemplateFormKey.groupName}`}
                maxLength={MAX_LENGTH_75}
                isMandatory
                placeholder={inputPlaceHolder}
                showCloseIcon
                requiredARIALabel={commonARIALabel.required}
                tooltipMessage={
                  commonValidationMessages.fieldSpecialCharactersTooltipMessage
                }
                tooltipHeaderTitle={
                  commonValidationMessages.allowedSpecialCharactersTitle
                }
              />
            </FormController>
          </MuiBox>
        </MuiGrid>
        <MuiTabs
          tabs={tabs}
          value={selectedTab}
          onChange={onTabChange}
          tabsTestIds={[tabIdResources, tabIdProgramTemplates]}
        >
          <MuiTabPanel
            panelId={`${pageId}-panel1`}
            value={selectedTab}
            index={0}
            ariaLabelledById={tabIdResources}
          >
            <FormProvider {...resourcesMethods}>
              <CreateResources isEdit={isEdit} />
            </FormProvider>
          </MuiTabPanel>
          <MuiTabPanel
            panelId={`${pageId}-panel2`}
            value={selectedTab}
            index={1}
            ariaLabelledById={tabIdProgramTemplates}
          >
            <FormProvider {...programTemplateMethods}>
              <CreateProgramTemplates
                countries={setApiDataToDropDownOption(countryListData, true)}
                programTemplates={setApiDataToDropDownOption(
                  programTemplateList,
                )}
                isEdit={isEdit}
              />
            </FormProvider>
          </MuiTabPanel>
        </MuiTabs>
        {showCancel && (
          <WarningConfirmationModal
            show={showCancel}
            onClose={closeModalHandler}
            onPrimaryButtonClick={cancelConfirmHandler}
            secondaryButtonTitle={no}
            primaryButtonTitle={yes}
            labelContent={
              isCancelButtonClickedRef.current
                ? cancelActionText
                : getLabel(
                    isEdit
                      ? groupUpdateActionText
                      : saveClientSpecificContentActionText,
                  )
            }
          />
        )}
        {showSuccessModal && (
          <MuiModal
            onClose={handleSuccessModalClose}
            title={getLabel(success)}
            open={showSuccessModal}
            primaryButtonTitle={ok}
            onPrimaryButtonClick={handleSuccessModalPrimaryClick}
          >
            <AddClientSpecificContentWrapper>
              <MuiTypography variant="h3" className="successMessage-text">
                {getLabel(successMessage)}
              </MuiTypography>
              <MuiTable
                columns={columns}
                tableResults={getTableDisplayData()}
                tableHeaderForm={[]}
                pageId={pageId}
                isFooterVisible={false}
                noDataInApi={isEmpty(addResourcesAndProgramTemplatesData)}
                priorityResults={[]}
              />
            </AddClientSpecificContentWrapper>
          </MuiModal>
        )}
      </AddClientSpecificContentWrapper>
    </MuiModal>
  );
};
